import React, { useEffect, useMemo, useState } from 'react';
import '../styles.css';
import { db } from '../firebase';
import { addDoc, collection, getDocs, serverTimestamp, query, where, onSnapshot, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import emailjs from 'emailjs-com';

const Store = () => {
  const { user, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bids, setBids] = useState({});
  const [myBidsByImage, setMyBidsByImage] = useState({});
  const [sort, setSort] = useState('recent');
  const [hiddenSet, setHiddenSet] = useState(new Set());
  const [likeCounts, setLikeCounts] = useState({});
  const [storeSet, setStoreSet] = useState(new Set());
  const [storeDocsBySrc, setStoreDocsBySrc] = useState({});

  // Static file paths from the web app (current gallery assets)
  const staticItems = useMemo(() => {
    const s = [
      // Landscape
      { src: "/TzofitPaintings/Landscape/Landscape48x72.jpg", description: "Acrylic on Canvas 72 x 48 inches" },
      { src: "/TzofitPaintings/Landscape/IMG_8027.jpeg", description: "Acrylic on Canvas 48 x 36 inches" },
      { src: "/TzofitPaintings/Landscape/Landscape.jpg", description: "Acrylic on Canvas 36 x 48 inches" },
      { src: "/TzofitPaintings/Landscape/AcryliconCanvas10x20inches.jpeg", description: "Acrylic on Canvas 10 x 20 inches (Sold)" },
      // Abstract (subset used in app now)
      { src: "/TzofitPaintings/Abstract/Acrylic_on_wood_panel_30x40_(76x101_cm).jpg", description: "Acrylic on Wood Panel 30 x 40 inches" },
      { src: "/TzofitPaintings/Abstract/EcocalypseAcryliconCanvas60x72in(150x180cm).jpg", description: "Ecoalpyse Acrylic on Canvas 60 x 72 inches (150 x 180 cm)" },
      { src: "/TzofitPaintings/Abstract/abstractBlue.jpeg", description: "Acrylic on Canvas 36 x 48 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_8645.jpg", description: "Acrylic on Canvas 30 x 40 inches" },
      { src: "/TzofitPaintings/Abstract/Acrylic_on_wood_panel_11x15in.jpg", description: "Acrylic on Wood Panel 11 x 15 inches" },
      { src: "/TzofitPaintings/Abstract/Acrylic_on_wood_panel_30x40in_(76x101_cm)_heic.jpg", description: "Acrylic on Wood Panel 30 x 40 inches" },
      { src: "/TzofitPaintings/Abstract/Acrylic_on_wood_panel_30x40in_(76x101_cm)heic.jpg", description: "Acrylic on Wood Panel 30 x 40 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_7694.jpg", description: "Acrylic on Canvas 18 x 36 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_7699.jpg", description: "Acrylic on Canvas 10 x 20 inches (25 x 50 cm)" },
      { src: "/TzofitPaintings/Abstract/IMG_7735.jpg", description: "Acrylic on Paper 17 x 20.5 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_7737.jpg", description: "Acrylic on Paper 22 x 30 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_8003.jpeg", description: "Acrylic on Canvas 16 x 20 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_8009.jpg", description: "Acrylic on Canvas 16 x 20 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_8059.jpeg", description: "Acrylic on Canvas 72 x 48 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_8014.jpeg", description: "Acrylic on Wood Panel 24 x 36 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_8019.jpg", description: "Acrylic on Canvas" },
      { src: "/TzofitPaintings/Abstract/IMG_8039.jpeg", description: "Acrylic on Canvas 48 x 72 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_8062.jpeg", description: "Acrylic on Canvas 18 x 36 inches" },
      { src: "/TzofitPaintings/Abstract/IMG_8074.jpeg", description: "Acrylic on Canvas 8 x 12 inches" },
      { src: "/TzofitPaintings/Abstract/Acrylic_on_canvas_10x20in_(25x50_cm).jpg", description: "Acrylic on Canvas 10 x 20 inches (25 x 50 cm)" },
      { src: "/TzofitPaintings/Abstract/Acryliconcanvas10x20in.(25x50cm)copy.jpg", description: "Acrylic on Canvas 10 x 20 inches (25 x 50 cm)" },
      { src: "/TzofitPaintings/Abstract/Acryliconcanvas10x20in(25x50cm)2.jpeg", description: "Acrylic on Canvas 10 x 20 inches (25 x 50 cm)" },
      { src: "/TzofitPaintings/Abstract/IMG_7675.jpg", description: "Acrylic on Canvas 10 x 20 inches (25 x 50 cm)" },
      // Realms
      { src: "/TzofitPaintings/Realms/RealmsAcryliconCanvas60x72in(150x180cm).jpeg", description: "Acrylic on Canvas 60 x 72 inches (150 x 180 cm)" },
      { src: "/TzofitPaintings/Realms/Acryliconcanvas10x20in(25x50cm)3.jpeg", description: "Acrylic on Canvas 10 x 20 inches (25 x 50 cm)" },
      { src: "/TzofitPaintings/Realms/Acryliconwoodpanel24x36in(60x90cm).jpg", description: "Acrylic on Wood Panel 24 x 36 inches (60 x 90 cm)" },
      // Patterns
      { src: "/TzofitPaintings/Patterns/patterns1.jpg", description: "Acrylic on Canvas" },
    ];
    return s.map((it) => ({ id: it.src, ...it }));
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'galleryImages'));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt || null }));
        // merge Firestore and static, prefer Firestore entries when src duplicates
        const bySrc = new Map();
        [...staticItems, ...data.map(d => ({ id: d.id, src: d.src, description: d.description, createdAt: d.createdAt }))].forEach((it) => {
          bySrc.set(it.src, { id: it.id || it.src, src: it.src, description: it.description || '', createdAt: it.createdAt || null });
        });
        let merged = Array.from(bySrc.values());
        if (hiddenSet.size > 0) merged = merged.filter(i => !hiddenSet.has(i.src));
        setItems(merged);
      } catch {
        // fallback to static assets only
        let merged = staticItems;
        if (hiddenSet.size > 0) merged = merged.filter(i => !hiddenSet.has(i.src));
        setItems(merged);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [staticItems, hiddenSet]);

  // Load current user's bids to display "Your bid"
  useEffect(() => {
    if (!user) { setMyBidsByImage({}); return; }
    const q = query(collection(db, 'bids'), where('userId', '==', user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const map = {};
      snap.forEach((d) => {
        const data = d.data();
        if (data.imageKey) map[data.imageKey] = data.amount;
      });
      setMyBidsByImage(map);
    });
    return () => unsub();
  }, [user]);

  // Watch hidden list from admin
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'hiddenImages'), (snap) => {
      const s = new Set();
      snap.forEach(d => { const v = d.data().src; if (v) s.add(v); });
      setHiddenSet(s);
    });
    return () => unsub();
  }, []);

  // Popularity counts from imageLikes
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'imageLikes'), (snap) => {
      const map = {};
      snap.forEach(d => {
        const { imageKey } = d.data() || {};
        if (!imageKey) return;
        map[imageKey] = (map[imageKey] || 0) + 1;
      });
      setLikeCounts(map);
    });
    return () => unsub();
  }, []);

  // Track store membership for admin
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'storeItems'), (snap) => {
      const s = new Set();
      const by = {};
      snap.forEach(d => { const data = d.data() || {}; if (data.src) { s.add(data.src); by[data.src] = { id: d.id, ...data }; } });
      setStoreSet(s);
      setStoreDocsBySrc(by);
    });
    return () => unsub();
  }, []);

  const handleBidChange = (id, value) => {
    setBids((b) => ({ ...b, [id]: value }));
  };

  const handleSubmitBid = async (id) => {
    const value = bids[id];
    if (!value) return;
    const item = items.find((it) => it.id === id);
    try {
      await addDoc(collection(db, 'bids'), {
        imageKey: item?.src || '',
        amount: parseFloat(value),
        userId: user?.uid || null,
        userEmail: user?.email || null,
        createdAt: serverTimestamp(),
      });

      // Send email notification to admin via EmailJS using same template as Contact
      try {
        await emailjs.send(
        'service_fe5vvvr', // serviceID from Contact.js
        'template_btklpob', // templateID from Contact.js
        {
          from_name: user?.email || 'Anonymous bidder',
          reply_to: user?.email || 'noreply@example.com',
          message: `New bid on ${item?.src}: $${value}`,
        },
        'YoAg6vMFxzWSRVivA' // userID from Contact.js
        );
      } catch {}

      alert('Bid submitted. We will contact you by email.');
      setBids((b) => ({ ...b, [id]: '' }));
    } catch (e) {
      alert('Failed to submit bid. Please try again.');
    }
  };

  if (loading) {
    return (
      <section id="store" className="section store-section">
        <h2>Store</h2>
        <p>Loading...</p>
      </section>
    );
  }

  return (
    <section id="store" className="section store-section">
      <h2>Store</h2>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 10 }}>
        <button className="btn-outline" onClick={() => setSort('recent')} aria-pressed={sort==='recent'}>Sort by Recency</button>
        <button className="btn-outline" onClick={() => setSort('popular')} aria-pressed={sort==='popular'}>Sort by Likes</button>
      </div>
      <div className="image-grid">
        {[...items].sort((a, b) => {
          if (sort === 'popular') {
            const ca = likeCounts[a.src] || 0;
            const cb = likeCounts[b.src] || 0;
            return cb - ca;
          }
          // Recency: prefer createdAt (Firestore uploaded). Static items (no date) go to bottom.
          const ta = a.createdAt && a.createdAt.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
          const tb = b.createdAt && b.createdAt.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
          return tb - ta;
        }).map((item) => (
          <div key={item.id} className={`image-item ${/\(Sold\)/i.test(item.description || '') ? 'sold' : ''}`}>
            <img src={item.src} alt={item.description || 'Artwork'} className="gallery-image" />
            {/\(Sold\)/i.test(item.description || '') && (
              <div className="sold-overlay" aria-label="Sold">SOLD</div>
            )}
            <p className="image-description">{item.description || ''}</p>
            {isAdmin && (
              <div style={{ display: 'grid', gap: 6, justifyItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Update description"
                  value={bids[item.id + '-desc'] || ''}
                  onChange={(e) => setBids((b) => ({ ...b, [item.id + '-desc']: e.target.value }))}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', maxWidth: 520, width: '100%' }}
                />
                <button className="btn-outline" onClick={async () => {
                  try {
                    // Reuse imageMeta to override static/store descriptions globally
                    // Use hashed id similar to Gallery safeId
                    let hash = 5381; const src = item.src; for (let i = 0; i < src.length; i += 1) { hash = ((hash << 5) + hash) + src.charCodeAt(i); hash &= 0xffffffff; }
                    const id = `img_${(hash >>> 0).toString(16)}`;
                    await setDoc(doc(db, 'imageMeta', id), { src: item.src, description: (bids[item.id + '-desc'] || '').trim() }, { merge: true });
                    alert('Description updated');
                  } catch { alert('Failed to update description'); }
                }}>Save Description</button>
              </div>
            )}
            {myBidsByImage[item.src] != null && (
              <p className="image-description">Your bid: ${myBidsByImage[item.src]}</p>
            )}
            {user && !isAdmin && !(/\(Sold\)/i.test(item.description || '')) && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Enter bid (USD)"
                  value={bids[item.id] || ''}
                  onChange={(e) => handleBidChange(item.id, e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                <button className="btn-outline" onClick={() => handleSubmitBid(item.id)}>Submit bid</button>
              </div>
            )}
            {isAdmin && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="btn-outline" onClick={async () => {
                  try {
                    const info = storeDocsBySrc[item.src];
                    if (info?.id) {
                      await deleteDoc(doc(db, 'storeItems', info.id));
                      alert('Removed from store');
                    } else {
                      // Not in store: hide static item from Store grid
                      await addDoc(collection(db, 'hiddenImages'), { src: item.src });
                      alert('Hidden from store');
                    }
                  } catch { alert('Failed to remove/hide'); }
                }}>Remove from Store</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
          <button className="btn-outline" onClick={async () => {
            try {
              const toRemove = items.filter(it => storeSet.has(it.src));
              await Promise.all(toRemove.map(async it => { const info = storeDocsBySrc[it.src]; if (info?.id) await deleteDoc(doc(db, 'storeItems', info.id)); }));
              alert('All visible removed from store');
            } catch { alert('Batch remove failed'); }
          }}>Remove all visible from Store</button>
        </div>
      )}
    </section>
  );
};

export default Store;